import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import StarDivider from "@/components/StarDivider";
import { Calendar, MapPin, Clock, Ticket, HeartHandshake } from "lucide-react";

const KICKOFF_EVENT = {
  title: "Veterans for Indiana — Kickoff 2026 Fundraiser",
  date: "Wednesday, June 3, 2026",
  time: "6:00 PM – 9:00 PM",
  venue: "Mashcraft",
  address: "2205 N Delaware St, Indianapolis, IN 46205",
  image: "/assets/events/kickoff-2026.jpg",
  donateUrl: "https://secure.actblue.com/donate/vfi2026kickoff",
  rsvpUrl: "https://secure.actblue.com/donate/vfi2026kickoff",
  description:
    "Join Veterans for Indiana as we kick off the 2026 cycle. Meet our team, hear from veteran leaders, and help us recruit, endorse, and elect veterans and military family members across Indiana. Free to attend — donations welcome online or at the event.",
};

export default function Events() {
  return (
    <>
      <SEO
        title="Events | Veterans for Indiana"
        description="Join the Veterans for Indiana Kickoff 2026 Fundraiser on Wednesday, June 3, 2026 at Mashcraft in Indianapolis. Free to attend, donations welcome."
        path="/events"
      />

      <section className="bg-cream py-16">
        <div className="container-x text-center max-w-3xl mx-auto">
          <Reveal>
            <h1 className="h-display text-5xl sm:text-6xl text-navy">Events</h1>
            <div className="mt-4 flex justify-center"><StarDivider /></div>
            <p className="mt-6 text-navy-deep/80 leading-relaxed">
              Meet endorsed candidates, attend fundraisers, and connect with veterans and military families across Indiana.
            </p>
          </Reveal>
        </div>
      </section>

      {/* UPCOMING EVENT */}
      <section className="bg-white py-16" aria-labelledby="upcoming-title">
        <div className="container-x">
          <Reveal>
            <h2 id="upcoming-title" className="h-display text-2xl sm:text-3xl text-navy">Upcoming Event</h2>
            <span className="mt-2 inline-block h-[3px] w-16 bg-green-brand" />
          </Reveal>

          <Reveal delay={1}>
            <article
              className="mt-10 rounded-2xl overflow-hidden ring-1 ring-gray-200 bg-white shadow-sm grid lg:grid-cols-2"
              data-testid="kickoff-2026-event"
            >
              {/* IMAGE */}
              <a
                href={KICKOFF_EVENT.rsvpUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="relative block bg-cream group p-4 sm:p-6 flex items-center justify-center"
                aria-label="RSVP / Donate to the Veterans for Indiana Kickoff 2026 Fundraiser"
              >
                <img
                  src={KICKOFF_EVENT.image}
                  alt="Veterans for Indiana Kickoff 2026 Fundraiser — June 3, 2026, Mashcraft, Indianapolis"
                  className="w-full h-auto max-h-[640px] object-contain transition-transform duration-500 group-hover:scale-[1.02]"
                  loading="lazy"
                />
                <span className="absolute top-4 left-4 bg-green-brand text-white font-display tracking-widest text-[10px] uppercase px-2.5 py-1 rounded shadow">
                  Upcoming
                </span>
              </a>

              {/* DETAILS */}
              <div className="p-8 lg:p-10 flex flex-col">
                <p className="font-display tracking-[0.22em] text-xs uppercase text-green-brand">2026 Cycle</p>
                <h3 className="mt-2 h-display text-3xl sm:text-4xl text-navy leading-tight">
                  {KICKOFF_EVENT.title}
                </h3>
                <span className="mt-3 inline-block h-[3px] w-16 bg-green-brand" />

                <ul className="mt-6 space-y-3 text-navy-deep/85">
                  <li className="flex items-start gap-3">
                    <Calendar className="text-green-brand mt-0.5 shrink-0" size={20} />
                    <span><span className="font-semibold text-navy">Date:</span> {KICKOFF_EVENT.date}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Clock className="text-green-brand mt-0.5 shrink-0" size={20} />
                    <span><span className="font-semibold text-navy">Time:</span> {KICKOFF_EVENT.time}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <MapPin className="text-green-brand mt-0.5 shrink-0" size={20} />
                    <span>
                      <span className="font-semibold text-navy">Location:</span> {KICKOFF_EVENT.venue}
                      <br />
                      <span className="text-sm text-navy-deep/70">{KICKOFF_EVENT.address}</span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Ticket className="text-green-brand mt-0.5 shrink-0" size={20} />
                    <span><span className="font-semibold text-navy">Admission:</span> Free to attend — donations welcome online or at the event.</span>
                  </li>
                </ul>

                <p className="mt-6 text-navy-deep/80 leading-relaxed">
                  {KICKOFF_EVENT.description}
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href={KICKOFF_EVENT.rsvpUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                    data-testid="kickoff-rsvp-cta"
                  >
                    <HeartHandshake size={18} /> RSVP / Donate
                  </a>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(KICKOFF_EVENT.venue + " " + KICKOFF_EVENT.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary"
                    data-testid="kickoff-directions-cta"
                  >
                    <MapPin size={18} /> Get Directions
                  </a>
                </div>
              </div>
            </article>
          </Reveal>
        </div>
      </section>

      {/* HOST AN EVENT */}
      <section className="bg-gray-soft py-16">
        <div className="container-x grid md:grid-cols-2 gap-8 items-center">
          <Reveal>
            <h2 className="h-display text-2xl sm:text-3xl text-navy">Host an Event</h2>
            <span className="mt-2 inline-block h-[3px] w-16 bg-green-brand" />
            <p className="mt-4 text-navy-deep/80 leading-relaxed max-w-lg">
              Want to host a meet-and-greet, fundraiser, or community event for one of our endorsed candidates? We&apos;d love to help.
            </p>
          </Reveal>
          <Reveal delay={1} className="md:justify-self-end">
            <Link to="/get-involved" className="btn btn-secondary" data-testid="events-host-cta">Host an Event</Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
